/* eslint-disable react-refresh/only-export-components -- provider + re-exports */
import { useCallback, useMemo, useState, lazy, Suspense } from 'react';
import { AiAssistantPanelContext } from './aiAssistantPanelContext';
import { useHasPermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';

const AiAssistantPanelLazy = lazy(() =>
    import('../components/AiAssistantPanel').then((m) => ({
        default: m.AiAssistantPanel,
    })),
);

/**
 * Gates the whole feature on USE_AI_ASSISTANT, which only Admin, Executive,
 * Task Force Chair and Pillar Chair hold. Without it the panel context is not
 * provided at all, so AiAssistantButton and AiAssistantNavItem — both of which
 * use the *optional* hook and render null without a panel — disappear from every
 * layout at once.
 *
 * Gating here rather than in each layout is deliberate: six layouts mount this
 * provider, and a per-layout check would put the policy in six places for the
 * next one to miss. The panel bundle is lazy, so an ungated role never loads it.
 * The API enforces the same rule independently in routes/aiAssistant.route.js —
 * this only decides what is worth showing.
 */
export function AiAssistantProvider({ children }) {
    const canUseAssistant = useHasPermissions(PERMISSIONS.USE_AI_ASSISTANT);
    const [isPanelOpen, setPanelOpen] = useState(false);
    /** Lets a page open the assistant with a question pre-filled. */
    const [seedQuestion, setSeedQuestion] = useState(null);

    const openPanel = useCallback((question = null) => {
        setSeedQuestion(question);
        setPanelOpen(true);
    }, []);
    const closePanel = useCallback(() => setPanelOpen(false), []);
    const togglePanel = useCallback(() => setPanelOpen((open) => !open), []);
    const clearSeedQuestion = useCallback(() => setSeedQuestion(null), []);

    const value = useMemo(
        () => ({
            isPanelOpen,
            openPanel,
            closePanel,
            togglePanel,
            seedQuestion,
            clearSeedQuestion,
        }),
        [isPanelOpen, openPanel, closePanel, togglePanel, seedQuestion, clearSeedQuestion],
    );

    if (!canUseAssistant) return <>{children}</>;

    return (
        <AiAssistantPanelContext.Provider value={value}>
            {children}
            <Suspense fallback={null}>
                <AiAssistantPanelLazy />
            </Suspense>
        </AiAssistantPanelContext.Provider>
    );
}

export {
    useAiAssistantPanel,
    useAiAssistantPanelOptional,
} from './aiAssistantPanelContext';
